import {rewriteExampleBlocksForLegacy} from './example-legacy-rewrite';

const scalarType = '';

const branchInputs = new Set(['SUBSTACK', 'SUBSTACK2']);

const makeIdFactory = prefix => {
    let index = 0;
    return () => `${prefix}_${index++}`;
};

const slugify = value => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'item';

const escapeXml = value => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const isBlockDescriptor = value => Boolean(value && typeof value === 'object' && typeof value.opcode === 'string');

const normalizeInput = value => {
    if (value === null || typeof value === 'undefined') return null;
    if (Array.isArray(value)) {
        return {
            kind: 'stack',
            blocks: value
        };
    }
    if (typeof value === 'object' && value.kind) return value;
    if (isBlockDescriptor(value)) return value;
    if (typeof value === 'number') {
        return {
            kind: 'shadow',
            shadowType: 'math_number',
            fieldName: 'NUM',
            value: String(value)
        };
    }
    return {
        kind: 'shadow',
        shadowType: 'text',
        fieldName: 'TEXT',
        value: String(value)
    };
};

const variableKey = descriptor => [
    descriptor.scope || 'local',
    typeof descriptor.type === 'string' ? descriptor.type : scalarType,
    descriptor.name
].join('::');

const buildPreviewVariableMap = example => {
    const map = Object.create(null);
    (example.variables || []).forEach((descriptor, index) => {
        const normalized = {
            scope: descriptor.scope || 'local',
            type: typeof descriptor.type === 'string' ? descriptor.type : scalarType,
            name: descriptor.name,
            id: `preview_var_${slugify(descriptor.name)}_${index}`
        };
        map[variableKey(normalized)] = normalized;
    });
    return map;
};

const compileField = (fieldName, value, state) => {
    if (value && value.kind === 'variable') {
        const descriptor = {
            name: value.name,
            scope: value.scope || 'local',
            type: typeof value.type === 'string' ? value.type : scalarType
        };
        const variable = state.variableMap[variableKey(descriptor)];
        if (!variable) {
            throw new Error(`Missing variable definition for "${descriptor.name}"`);
        }
        return {
            name: fieldName,
            value: variable.name,
            id: variable.id,
            variableType: variable.type
        };
    }
    if (typeof value === 'object' && value && value.kind === 'field') {
        return {
            name: fieldName,
            value: String(value.value),
            id: value.id,
            variableType: value.variableType
        };
    }
    return {
        name: fieldName,
        value: String(value)
    };
};

const compileShadowBlock = (inputDescriptor, parentId, state) => {
    const id = state.nextId();
    return {
        id,
        blocks: [{
            id,
            opcode: inputDescriptor.shadowType,
            inputs: {},
            fields: {
                [inputDescriptor.fieldName]: {
                    name: inputDescriptor.fieldName,
                    value: String(inputDescriptor.value)
                }
            },
            next: null,
            parent: parentId,
            shadow: true,
            topLevel: false
        }]
    };
};

const compileStack = (blockList, parentId, topLevel, position, state) => {
    const compiledBlocks = [];
    let firstId = null;
    let previousId = null;
    blockList.forEach((blockDescriptor, index) => {
        const currentParentId = index === 0 ? parentId : previousId;
        const compiled = compileBlock(blockDescriptor, currentParentId, topLevel && index === 0, position, state);
        if (!firstId) firstId = compiled.id;
        if (previousId) {
            const previousBlock = compiledBlocks.find(block => block.id === previousId);
            previousBlock.next = compiled.id;
        }
        previousId = compiled.id;
        compiledBlocks.push(...compiled.blocks);
    });
    return {
        id: firstId,
        blocks: compiledBlocks
    };
};

const compileInput = (inputName, rawValue, parentId, state) => {
    const value = normalizeInput(rawValue);
    if (!value) return null;

    if (value.kind === 'stack') {
        const compiledStack = compileStack(value.blocks, parentId, false, null, state);
        return {
            input: {
                name: inputName,
                block: compiledStack.id,
                shadow: null
            },
            blocks: compiledStack.blocks
        };
    }

    if (value.kind === 'shadow') {
        const compiledShadow = compileShadowBlock(value, parentId, state);
        return {
            input: {
                name: inputName,
                block: compiledShadow.id,
                shadow: compiledShadow.id
            },
            blocks: compiledShadow.blocks
        };
    }

    if (isBlockDescriptor(value)) {
        const compiledBlock = compileBlock(value, parentId, false, null, state);
        return {
            input: {
                name: inputName,
                block: compiledBlock.id,
                shadow: null
            },
            blocks: compiledBlock.blocks
        };
    }

    return null;
};

const compileBlock = (descriptor, parentId, topLevel, position, state) => {
    const id = state.nextId();
    const block = {
        id,
        opcode: descriptor.opcode,
        inputs: {},
        fields: {},
        next: null,
        parent: parentId,
        shadow: descriptor.shadow === true,
        topLevel
    };

    if (topLevel && position) {
        block.x = position.x;
        block.y = position.y;
    }

    Object.entries(descriptor.fields || {}).forEach(([fieldName, value]) => {
        block.fields[fieldName] = compileField(fieldName, value, state);
    });

    const compiledChildren = [];
    Object.entries(descriptor.inputs || {}).forEach(([inputName, value]) => {
        const compiledInput = compileInput(inputName, value, id, state);
        if (!compiledInput) return;
        block.inputs[inputName] = compiledInput.input;
        compiledChildren.push(...compiledInput.blocks);
    });

    return {
        id,
        blocks: [block, ...compiledChildren]
    };
};

export const compileExampleBlocks = (example, options = {}) => {
    const state = {
        nextId: makeIdFactory(slugify(example.id || 'example')),
        variableMap: options.variableMap || Object.create(null)
    };
    const offsetX = options.offsetX || 0;
    const offsetY = options.offsetY || 0;
    const compiledBlocks = [];
    (example.scripts || []).forEach(script => {
        const compiled = compileStack(
            script.blocks,
            null,
            true,
            {
                x: script.x + offsetX,
                y: script.y + offsetY
            },
            state
        );
        compiledBlocks.push(...compiled.blocks);
    });
    return compiledBlocks;
};

const renderFieldXml = field => {
    const attributes = [`name="${escapeXml(field.name)}"`];
    if (field.id) {
        attributes.push(`id="${escapeXml(field.id)}"`);
    }
    if (typeof field.variableType === 'string') {
        attributes.push(`variabletype="${escapeXml(field.variableType)}"`);
    }
    return `<field ${attributes.join(' ')}>${escapeXml(field.value)}</field>`;
};

const renderBlockXml = (block, blockMap) => {
    const tagName = block.shadow ? 'shadow' : 'block';
    const attributes = [
        `type="${escapeXml(block.opcode)}"`,
        `id="${escapeXml(block.id)}"`
    ];
    if (block.topLevel && typeof block.x === 'number' && typeof block.y === 'number') {
        attributes.push(`x="${block.x}"`);
        attributes.push(`y="${block.y}"`);
    }

    const fieldXml = Object.keys(block.fields || {})
        .map(fieldName => renderFieldXml(block.fields[fieldName]))
        .join('');

    const inputXml = Object.keys(block.inputs || {}).map(inputName => {
        const input = block.inputs[inputName];
        const tag = branchInputs.has(inputName) ? 'statement' : 'value';
        const childBlock = blockMap[input.block];
        const shadowBlock = input.shadow ? blockMap[input.shadow] : null;
        const renderedBlocks = [];
        if (childBlock) {
            renderedBlocks.push(renderBlockXml(childBlock, blockMap));
        }
        if (shadowBlock && shadowBlock.id !== input.block) {
            renderedBlocks.push(renderBlockXml(shadowBlock, blockMap));
        }
        return `<${tag} name="${escapeXml(inputName)}">${renderedBlocks.join('')}</${tag}>`;
    }).join('');

    const nextXml = block.next && blockMap[block.next] ?
        `<next>${renderBlockXml(blockMap[block.next], blockMap)}</next>` :
        '';

    return `<${tagName} ${attributes.join(' ')}>${fieldXml}${inputXml}${nextXml}</${tagName}>`;
};

const variableToXml = variable => {
    const isLocal = variable.scope !== 'global';
    return `<variable type="${escapeXml(variable.type)}" id="${escapeXml(variable.id)}" islocal="${isLocal}" iscloud="false">${escapeXml(variable.name)}</variable>`;
};

export const blocksToXml = (blocks, variableMap = Object.create(null)) => {
    const blockMap = blocks.reduce((result, block) => {
        result[block.id] = block;
        return result;
    }, Object.create(null));
    const topLevelBlocks = blocks
        .filter(block => block.topLevel)
        .sort((left, right) => {
            const leftY = typeof left.y === 'number' ? left.y : 0;
            const rightY = typeof right.y === 'number' ? right.y : 0;
            if (leftY !== rightY) return leftY - rightY;
            const leftX = typeof left.x === 'number' ? left.x : 0;
            const rightX = typeof right.x === 'number' ? right.x : 0;
            return leftX - rightX;
        });
    const variables = Object.values(variableMap);
    const variablesXml = variables.map(variableToXml).join('');
    const scriptsXml = topLevelBlocks.map(block => renderBlockXml(block, blockMap)).join('');
    return `<xml xmlns="http://www.w3.org/1999/xhtml"><variables>${variablesXml}</variables>${scriptsXml}</xml>`;
};

export const getExamplePreviewXml = example => {
    const variableMap = buildPreviewVariableMap(example);
    const blocks = compileExampleBlocks(example, {variableMap});
    return blocksToXml(blocks, variableMap);
};

const buildRuntimeVariableMap = (example, target, stage) => {
    const map = Object.create(null);
    (example.variables || []).forEach((descriptor, index) => {
        const normalized = {
            name: descriptor.name,
            type: typeof descriptor.type === 'string' ? descriptor.type : scalarType,
            scope: descriptor.scope === 'global' || target.isStage ? 'global' : 'local'
        };
        const owner = normalized.scope === 'global' ? stage : target;
        const existingVariable = normalized.scope === 'global' ?
            owner.lookupVariableByNameAndType(normalized.name, normalized.type) :
            owner.lookupVariableByNameAndType(normalized.name, normalized.type, true);
        let variable = existingVariable;
        if (!variable) {
            const variableId = `example_var_${slugify(normalized.name)}_${index}_${Date.now().toString(36)}`;
            owner.createVariable(variableId, normalized.name, normalized.type, false);
            variable = owner.lookupVariableById(variableId);
        }
        map[variableKey(normalized)] = {
            ...normalized,
            id: variable.id,
            name: variable.name
        };
    });
    return map;
};

const getInsertionOffset = target => {
    const topLevelBlocks = Object.values(target.blocks._blocks || {})
        .filter(block => block.topLevel && typeof block.y === 'number');
    if (topLevelBlocks.length === 0) {
        return {
            x: 0,
            y: 0
        };
    }
    const maxY = Math.max(...topLevelBlocks.map(block => block.y || 0));
    return {
        x: 0,
        y: maxY + 260
    };
};

export const getPreferredTarget = vm => {
    if (vm.editingTarget && !vm.editingTarget.isStage) {
        return vm.editingTarget;
    }
    return vm.runtime.targets.find(target => !target.isStage) || vm.runtime.getTargetForStage();
};

export const insertExampleIntoVm = async (example, vm) => {
    const target = getPreferredTarget(vm);
    const stage = vm.runtime.getTargetForStage();
    const variableMap = buildRuntimeVariableMap(example, target, stage);
    const offset = getInsertionOffset(target);
    let blocks = compileExampleBlocks(example, {
        offsetX: offset.x,
        offsetY: offset.y,
        variableMap
    });

    if (vm.setActiveMinecraftExtension && vm.extensionManager.getActiveMinecraftExtension) {
        const active = vm.extensionManager.getActiveMinecraftExtension();
        if (active !== 'legacy') {
            await vm.setActiveMinecraftExtension('legacy');
        }
        blocks = rewriteExampleBlocksForLegacy(blocks);
    }

    await vm.shareBlocksToTarget(blocks, target.id);
    vm.setEditingTarget(target.id);
    vm.refreshWorkspace();
    return target;
};

